import * as React from 'react';
import { useEffect, useState } from 'react';
import { useData } from '../Form';
import Grid from '@mui/material/Grid';
import { TextField, FormControlLabel, FormControl, Checkbox, FormHelperText } from '@mui/material';
import OrderSummary from '../components/OrderSummary';
import { Divider } from '@mui/material';
import Typography from '@mui/material/Typography';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import BackAndNextButtons from '../components/BackAndNextButtons';
import { useStep } from '../Form';

const sources = [
  'Christians on Campus, Butte College',
  'Christians on Campus, De Anza College',
  'Christians on Campus, Foothill College',
  'Christians on Campus, Merced College',
  'Christians on Campus, SJSU',
  'Christians on Campus, UC Berkeley',
  'Christians on Campus, UC Davis',
  'Christians on Campus, UC Merced',
  'Christians on Campus, UC Santa Cruz',
  'Christian Students at Sac State',
  'Christian Students at SLO',
  'Christian Students at SFSU',
  'Christian Students at UNR',
  'CSUEB Bible Club',
  'SSU Bible Club',
  'Stanford Christian Students'
]

const filter = createFilterOptions();

export default function ReviewForm() {
  const { data, updateData } = useData()
  const {incrementStep} = useStep()

  const [values, setValues] = useState({
    hasFinancialSupport: data.hasFinancialSupport?? false,
    financialSupportAmount: data.financialSupportAmount ?? 0,
    financialSupportSource: data.financialSupportSource ?? '',
    servingOneEmail: data.servingOneEmail ?? '',
    servingOneName: data.servingOneName ?? '',
    comments: data.comments ?? '',
  }
  )

  const [error, setError] = useState({
    financialSupportSource: false,
    financialSupportAmount: false,
    servingOneEmail: false,
    servingOneName: false
  })

  let sum = data.products.reduce((n, { unit_amount }) => n + unit_amount.value, 0)
  useEffect(() => {
    if (values.financialSupportAmount > sum) {
      setValues({...values, ...{financialSupportAmount: sum }})
    }
    else if (values.financialSupportAmount < 0) {
      setValues({...values, ...{financialSupportAmount: 0 }})
    }
  }, [values.financialSupportAmount])


  function checkForErrors() {
    if (values.hasFinancialSupport){
      let newError = {
        financialSupportSource: values.financialSupportSource === "",
        financialSupportAmout: values.financialSupportAmount <= 0,
        servingOneEmail: values.servingOneEmail === "" || !(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i).test(values.servingOneEmail),
        servingOneName: values.servingOneName === "",
      }

      setError(newError);
      return Object.keys(newError).some((k) => newError[k]);
    }
    else{
      setError({
        financialSupportSource: false,
        financialSupportAmount: false,
        servingOneEmail: false,
        servingOneName: false
      })
      return false
    }

  }

  function handleNext() {
    if (checkForErrors()) {
      // If there are errors, don't proceed
      return;
    } else {
      incrementStep(); // Proceed to the next step
      updateData(values); // Save the current form data
    }
  }

  return (
    <React.Fragment>
      <OrderSummary />
      <Divider />
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox checked={values.hasFinancialSupport}
                onChange={() => setValues({ ...values, ...{ hasFinancialSupport: !values.hasFinancialSupport }})
              } />}
            label="I have financial support from my local campus club / church" />
        </Grid>
        <Grid item xs={12}>
          {values.hasFinancialSupport ?
            <>
              <Typography variant="h6" gutterBottom>
                Financial Support Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    error={error.financialSupportAmount}
                    helperText={error.financialSupportAmount ? "Cannot be left blank" : ''}
                    id="financialSupportAmount"
                    name="financialSupportAmount"
                    label="Financial Support Amount"
                    fullWidth
                    value={values.financialSupportAmount}
                    type="number"
                    variant="standard"
                    onChange={(e) => {
                      setValues({ ...values, ...{ financialSupportAmount: e.target.value } })
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small" required>
                    <Autocomplete
                      value={values.financialSupportSource}
                      onChange={(event, newValue) => {
                        if (typeof newValue === 'string') {
                          setValues({ ...values, ...{ financialSupportSource: newValue } })
                        } else if (newValue && newValue.inputValue) {
                          // Create a new value from the user input
                          setValues({ ...values, ...{ financialSupportSource: newValue.inputValue } })
                        } else {
                          setValues({ ...values, ...{ financialSupportSource: newValue } })
                        }
                      }}
                      filterOptions={(options, params) => {
                        const filtered = filter(options, params);

                        const { inputValue } = params;
                        // Suggest the creation of a new value
                        const isExisting = options.some((option) => inputValue === option);
                        if (inputValue !== '' && !isExisting) {
                          filtered.push(
                            inputValue);
                        }

                        return filtered;
                      }}
                      required={true}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                      id="Support Source"
                      options={sources}
                      getOptionLabel={(option) => {
                        // Value selected with enter, right from the input
                        if (typeof option === 'string') {
                          return option;
                        }
                        // Add "xxx" option created dynamically
                        if (option.inputValue) {
                          return option.inputValue;
                        }
                        // Regular option
                        return option.name;
                      }}
                      renderOption={(props, option) => <li {...props}>{option}</li>}
                      freeSolo
                      renderInput={(params) => (
                        <TextField {...params} label="Please select support source" error={error.financialSupportSource} />
                      )}
                    />
                    <FormHelperText>If your club or church is not listed, please type it in the box and press enter</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    error={error.servingOneName}
                    helperText={error.servingOneName ? "Cannot be left blank" : ''}
                    id="servingone"
                    name="servingone"
                    label="First and Last Name of Serving One"
                    fullWidth
                    variant="standard"
                    value={values.servingOneName}
                    onChange={(e) => {
                      setValues({ ...values, ...{ servingOneName: e.target.value } })
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    error={error.servingOneEmail}
                    helperText={error.servingOneEmail ? "Invalid email" : ''}
                    id="servingOneEmail"
                    name="servingOneEmail"
                    label="Serving One Email"
                    value={values.servingOneEmail}
                    fullWidth
                    variant="standard"
                    onChange={(e) => {
                      setValues({ ...values, ...{ servingOneEmail: e.target.value } })
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <TextField
                      id="comments"
                      name="comments"
                      label="comments"
                      fullWidth
                      multiline
                      variant="standard"
                      value={values.comments}
                      onChange={(e) => {
                        setValues({ ...values, ...{ comments: e.target.value } })
                      }}
                    />
                    <FormHelperText>Please let us know if you have any questions, comments or concerns</FormHelperText>
                  </FormControl>

                </Grid>
              </Grid>
            </> : <></>
          }
        </Grid>
      </Grid>
      <BackAndNextButtons handleNext={handleNext}></BackAndNextButtons>
    </React.Fragment>
  );
}
